// src/components/admin/ExclusionPreviewModal.jsx
// Enhanced modal for showing customer lists with exclusion details

import React, { useState, useEffect } from 'react';
import '../../styles/customer-preview-modal.css';

function ExclusionPreviewModal({ isOpen, onClose, previewData, modalType, campaignName }) {
  const [currentPage, setCurrentPage] = useState(1);
  const [filterText, setFilterText] = useState('');
  const [sortField, setSortField] = useState('displayName');
  const [sortDirection, setSortDirection] = useState('asc');
  const customersPerPage = 100;

  useEffect(() => {
    setCurrentPage(1);
  }, [filterText]);

  if (!isOpen || !previewData) return null;

  // Determine which customer list to show based on modalType
  let customersToShow = [];
  let excludedCustomers = [];
  let modalTitle = 'Customer Preview';
  let showExclusionColumn = false;

  switch(modalType) {
    case 'unsubscribe':
      customersToShow = previewData.eligibleCustomers || [];
      excludedCustomers = previewData.unsubscribedCustomers || [];
      modalTitle = 'After Excluding Unsubscribed Customers';
      showExclusionColumn = false;
      break;
    case 'frequency':
      customersToShow = previewData.eligibleCustomers || [];
      excludedCustomers = [
        ...(previewData.unsubscribedCustomers || []),
        ...(previewData.frequencyExcludedCustomers || [])
      ];
      modalTitle = 'After Excluding Frequency Limits';
      showExclusionColumn = false;
      break;
    case 'recurrence':
      customersToShow = previewData.eligibleCustomers || [];
      excludedCustomers = [
        ...(previewData.unsubscribedCustomers || []),
        ...(previewData.frequencyExcludedCustomers || []),
        ...(previewData.recurrenceExcludedCustomers || [])
      ];
      modalTitle = 'After Excluding Previous Recipients';
      showExclusionColumn = false;
      break;
    case 'final':
      customersToShow = previewData.eligibleCustomers || [];
      excludedCustomers = [
        ...(previewData.unsubscribedCustomers || []),
        ...(previewData.frequencyExcludedCustomers || []),
        ...(previewData.recurrenceExcludedCustomers || [])
      ];
      modalTitle = 'Final Customer List (All Exclusions Applied)';
      showExclusionColumn = false;
      break;
    default:
      customersToShow = previewData.eligibleCustomers || [];
  }

  const totalCount = previewData.totalCount || 0;
  const eligibleCount = customersToShow.length;
  const excludedCount = excludedCustomers.length;

  const filteredCustomers = customersToShow.filter(customer => {
    if (!filterText) return true;
    const searchLower = filterText.toLowerCase();
    return (
      customer.displayName?.toLowerCase().includes(searchLower) ||
      customer.email?.toLowerCase().includes(searchLower)
    );
  });

  const sortedCustomers = [...filteredCustomers].sort((a, b) => {
    const aVal = a[sortField] || '';
    const bVal = b[sortField] || '';

    if (sortDirection === 'asc') {
      return aVal.localeCompare(bVal);
    } else {
      return bVal.localeCompare(aVal);
    }
  });

  const totalPages = Math.ceil(sortedCustomers.length / customersPerPage);
  const startIndex = (currentPage - 1) * customersPerPage;
  const endIndex = startIndex + customersPerPage;
  const currentCustomers = sortedCustomers.slice(startIndex, endIndex);

  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const handleExportCSV = () => {
    const headers = ['Name', 'Email'];
    const csvRows = [headers.join(',')];

    sortedCustomers.forEach(customer => {
      const row = [
        `"${customer.displayName || ''}"`,
        `"${customer.email || ''}"`
      ];
      csvRows.push(row.join(','));
    });

    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);

    const date = new Date().toISOString().split('T')[0];
    const filename = `campaign-recipients-${campaignName.replace(/[^a-z0-9]/gi, '-').toLowerCase()}-${date}.csv`;

    link.setAttribute('href', url);
    link.setAttribute('download', filename);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const coverage = totalCount > 0 ? ((eligibleCount / totalCount) * 100).toFixed(1) : '0.0';

  return (
    <div className="modal-overlay">
      <div className="customer-preview-modal">
        <div className="modal-header">
          <h3>{modalTitle}</h3>
          <button className="modal-close-btn" onClick={onClose} aria-label="Close">
            ×
          </button>
        </div>

        <div className="modal-summary">
          <div className="summary-item">
            <span className="summary-label">Will Receive Campaign</span>
            <span className="summary-value eligible-count">{eligibleCount}</span>
          </div>
          <div className="summary-item">
            <span className="summary-label">Excluded</span>
            <span className="summary-value excluded-count">{excludedCount}</span>
          </div>
          <div className="summary-item">
            <span className="summary-label">Total Customers</span>
            <span className="summary-value">{totalCount}</span>
          </div>
          <div className="summary-item">
            <span className="summary-label">Coverage</span>
            <span className="summary-value coverage">{coverage}%</span>
          </div>
        </div>

        {/* Exclusion Summary */}
        {(modalType === 'unsubscribe' || modalType === 'frequency' || modalType === 'recurrence' || modalType === 'final') && (
          <div className="exclusion-summary">
            {previewData.unsubscribedCount > 0 && (
              <div className="exclusion-info">
                <span className="exclusion-icon">✉️</span>
                <span className="exclusion-text">
                  <strong>{previewData.unsubscribedCount}</strong> customer(s) excluded due to unsubscribe preferences
                </span>
              </div>
            )}
            {previewData.frequencyExcludedCount > 0 && (
              <div className="exclusion-info">
                <span className="exclusion-icon">🚫</span>
                <span className="exclusion-text">
                  <strong>{previewData.frequencyExcludedCount}</strong> customer(s) excluded due to frequency limits
                  {previewData.frequencySettings && (
                    <span className="exclusion-detail">
                      {' '}(max {previewData.frequencySettings.maxFrequency} per {previewData.frequencySettings.interval})
                    </span>
                  )}
                </span>
              </div>
            )}
            {previewData.recurrenceExcludedCount > 0 && (
              <div className="exclusion-info">
                <span className="exclusion-icon">🔄</span>
                <span className="exclusion-text">
                  <strong>{previewData.recurrenceExcludedCount}</strong> customer(s) excluded as previous recipients
                </span>
              </div>
            )}
            {modalType === 'final' && previewData.summary && (
              <div className="exclusion-summary-total">
                <strong>Total Exclusion Rate:</strong> {previewData.summary.exclusionRate}%
              </div>
            )}
          </div>
        )}

        <div className="modal-body">
          {eligibleCount > 0 ? (
            <>
              <button
                className="export-csv-btn"
                onClick={handleExportCSV}
                disabled={sortedCustomers.length === 0}
              >
                📥 Export to CSV
              </button>

              <div className="table-filter">
                <input
                  type="text"
                  placeholder="Search by name or email..."
                  value={filterText}
                  onChange={(e) => setFilterText(e.target.value)}
                />
                {filterText && (
                  <button
                    className="filter-clear-btn"
                    onClick={() => setFilterText('')}
                  >
                    Clear
                  </button>
                )}
              </div>

              {currentCustomers.length > 0 ? (
                <>
                  <div className="customer-table-container">
                    <table className="customer-table">
                      <thead>
                        <tr>
                          <th
                            onClick={() => handleSort('displayName')}
                            className={sortField === 'displayName' ? `sorted-${sortDirection}` : ''}
                          >
                            Customer Name
                            <span className="sort-indicator"></span>
                          </th>
                          <th
                            onClick={() => handleSort('email')}
                            className={sortField === 'email' ? `sorted-${sortDirection}` : ''}
                          >
                            Email Address
                            <span className="sort-indicator"></span>
                          </th>
                        </tr>
                      </thead>
                      <tbody>
                        {currentCustomers.map((customer, index) => (
                          <tr key={customer.id || index}>
                            <td>{customer.displayName || 'N/A'}</td>
                            <td>{customer.email || 'N/A'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {totalPages > 1 && (
                    <div className="pagination">
                      <button
                        className="pagination-btn"
                        onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                        disabled={currentPage === 1}
                      >
                        Previous
                      </button>

                      <span className="pagination-info">
                        Page {currentPage} of {totalPages}
                      </span>

                      <button
                        className="pagination-btn"
                        onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                        disabled={currentPage === totalPages}
                      >
                        Next
                      </button>
                    </div>
                  )}
                </>
              ) : (
                <div className="empty-state">
                  <div className="empty-state-icon">🔍</div>
                  <p className="empty-state-text">No customers match your search</p>
                </div>
              )}
            </>
          ) : (
            <div className="empty-state">
              <div className="empty-state-icon">👥</div>
              <p className="empty-state-text">
                {excludedCount > 0
                  ? 'All targeted customers have been excluded based on frequency or recurrence settings'
                  : 'No eligible recipients found for the selected criteria'}
              </p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default ExclusionPreviewModal;
